<template>
  <div>
    <div v-if="isLoadingConfig" class="text-center py-12">
      <b-spinner variant="primary" label="กำลังโหลด..."></b-spinner>
      <span class="text-muted font-weight-bold mt-3 d-block">
        กำลังโหลดการตั้งค่าแต้มหมดอายุ...
      </span>
    </div>

    <template v-else>
      <b-form novalidate @submit.stop.prevent="onSubmit">
        <b-row class="mb-5">
          <b-col sm="3">
            <label>
              <h5>อายุของแต้ม <span class="text-danger">*</span></h5>
            </label>
          </b-col>
          <b-col sm="9">
            <b-form-radio
              v-model="form.is_enabled"
              :value="false"
              name="point-expiration-mode"
              class="mb-1"
              :disabled="isSaving"
            >
              <span class="font-weight-bold">ไม่มีวันหมดอายุ</span>
            </b-form-radio>
            <small class="text-muted d-block mb-5 ml-6">
              แต้มที่ได้รับจะใช้แลกของรางวัลได้ตลอด
            </small>

            <b-form-radio
              v-model="form.is_enabled"
              :value="true"
              name="point-expiration-mode"
              class="mb-3"
              :disabled="isSaving"
            >
              <span class="font-weight-bold">กำหนดวันหมดอายุ</span>
            </b-form-radio>
            <div class="ml-6">
              <b-input-group append="เดือน" style="max-width: 240px">
                <b-form-input
                  v-model.number="form.expiration_months"
                  type="number"
                  :min="minExpirationMonths"
                  :max="maxExpirationMonths"
                  :state="expirationMonthsState"
                  :disabled="!form.is_enabled || isSaving"
                  aria-describedby="expiration-months-feedback"
                ></b-form-input>
              </b-input-group>
              <b-form-invalid-feedback id="expiration-months-feedback" :state="expirationMonthsState">
                {{ expirationMonthsError }}
              </b-form-invalid-feedback>
              <small class="text-muted d-block mt-1">
                * กำหนดได้ {{ minExpirationMonths }} - {{ maxExpirationMonths }} เดือน
                แต้มจะหมดอายุในวันสุดท้ายของเดือนที่ครบกำหนด
              </small>
            </div>
          </b-col>
        </b-row>

        <b-row class="mb-5">
          <b-col sm="3">
            <label>
              <h5>ผลของการตั้งค่า</h5>
            </label>
          </b-col>
          <b-col sm="9">
            <div class="bg-light rounded p-5">
              <template v-if="form.is_enabled && sampleExpirationDate">
                <div class="font-weight-bold mb-2">
                  ตัวอย่าง: ได้รับแต้มวันที่ {{ formatThaiDate(sampleReceivedDate) }}
                  <span class="text-primary">
                    → หมดอายุ {{ formatThaiDate(sampleExpirationDate) }}
                  </span>
                </div>
                <ul class="text-muted mb-0 pl-5">
                  <li>ใช้กับแต้มที่ได้รับหลังจากกดบันทึกเท่านั้น</li>
                  <li>แต้มที่ได้รับไปแล้วยังใช้วันหมดอายุเดิม ไม่คำนวณใหม่</li>
                  <li>แต้มที่ได้รับตอนที่ตั้งเป็น "ไม่มีวันหมดอายุ" จะไม่มีวันหมดอายุตลอดไป</li>
                  <li>แต้มหมดอายุลดเฉพาะ Available Point ไม่กระทบ Lifetime Point และระดับสมาชิก</li>
                </ul>
              </template>
              <template v-else-if="!form.is_enabled">
                <div class="font-weight-bold mb-2">แต้มไม่มีวันหมดอายุ</div>
                <ul class="text-muted mb-0 pl-5">
                  <li>แต้มที่ได้รับหลังจากกดบันทึกจะไม่มีวันหมดอายุ</li>
                  <li>แต้มที่ได้รับไปแล้วยังใช้วันหมดอายุเดิม</li>
                </ul>
              </template>
              <span v-else class="text-muted">ระบุระยะเวลาหมดอายุเพื่อดูตัวอย่าง</span>
            </div>
          </b-col>
        </b-row>

        <div class="d-flex justify-content-end">
          <b-button
            class="font-weight-bold mr-3"
            :disabled="!isDirty || isSaving"
            @click.prevent="resetForm"
          >
            ยกเลิก
          </b-button>
          <b-button
            type="submit"
            variant="primary"
            class="font-weight-bold"
            :disabled="!isDirty || isSaving"
          >
            <b-spinner v-if="isSaving" small class="mr-2"></b-spinner>
            บันทึก
          </b-button>
        </div>
      </b-form>
    </template>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import {
  MIN_EXPIRATION_MONTHS,
  MAX_EXPIRATION_MONTHS,
  calculateExpirationDate,
  validateExpirationMonths,
  describePointLifetime,
  formatThaiDate
} from "../point-expire.helper";
import {
  fetchPointExpireConfig,
  savePointExpireConfig
} from "../point-expire.api";

const DEFAULT_EXPIRATION_MONTHS = 12;

export default {
  name: "PointExpireConfig",
  props: {
    companyId: {
      type: [Number, String],
      default: null
    }
  },
  data() {
    return {
      minExpirationMonths: MIN_EXPIRATION_MONTHS,
      maxExpirationMonths: MAX_EXPIRATION_MONTHS,
      isLoadingConfig: false,
      isSaving: false,
      savedConfig: null,
      form: {
        is_enabled: false,
        expiration_months: DEFAULT_EXPIRATION_MONTHS
      },
      sampleReceivedDate: new Date()
    };
  },
  computed: {
    expirationMonthsError() {
      return this.form.is_enabled
        ? validateExpirationMonths(this.form.expiration_months)
        : null;
    },
    expirationMonthsState() {
      return this.expirationMonthsError ? false : null;
    },
    sampleExpirationDate() {
      if (this.expirationMonthsError) return null;
      return calculateExpirationDate(this.sampleReceivedDate, this.form.expiration_months);
    },
    isDirty() {
      if (!this.savedConfig) return false;
      if (this.form.is_enabled !== this.savedConfig.is_enabled) return true;
      return (
        this.form.is_enabled &&
        Number(this.form.expiration_months) !== Number(this.savedConfig.expiration_months)
      );
    }
  },
  watch: {
    companyId() {
      this.loadConfig();
    }
  },
  mounted() {
    this.loadConfig();
  },
  methods: {
    formatThaiDate,
    describePointLifetime,
    loadConfig({ showSpinner = true } = {}) {
      this.isLoadingConfig = showSpinner;
      return fetchPointExpireConfig(this.companyId)
        .then((config) => {
          this.savedConfig = config;
          this.resetForm();
        })
        .catch((error) => {
          Swal.fire({
            icon: "error",
            title: "โหลดการตั้งค่าแต้มหมดอายุไม่สำเร็จ",
            text: error.message,
            confirmButtonText: "ตกลง"
          });
        })
        .finally(() => {
          this.isLoadingConfig = false;
        });
    },
    resetForm() {
      this.form = {
        is_enabled: !!this.savedConfig.is_enabled,
        expiration_months:
          this.savedConfig.expiration_months || DEFAULT_EXPIRATION_MONTHS
      };
    },
    confirmSave() {
      return Swal.fire({
        icon: "question",
        title: "ยืนยันการบันทึก",
        html: `
          <div class="text-left">
            <p class="mb-1">เดิม: <b>${this.describePointLifetime(this.savedConfig)}</b></p>
            <p class="mb-4">ใหม่: <b class="text-primary">${this.describePointLifetime(this.form)}</b></p>
            <p class="text-muted mb-0">
              การตั้งค่าใหม่มีผลกับแต้มที่ได้รับหลังจากบันทึกเท่านั้น
              แต้มที่ได้รับไปแล้วยังใช้วันหมดอายุเดิม
            </p>
          </div>`,
        showCancelButton: true,
        confirmButtonText: "บันทึก",
        cancelButtonText: "ยกเลิก",
        reverseButtons: true
      });
    },
    onSubmit() {
      if (this.isSaving || !this.isDirty) return;
      if (this.expirationMonthsError) {
        Swal.fire({
          icon: "warning",
          text: this.expirationMonthsError,
          confirmButtonText: "ตกลง"
        });
        return;
      }

      this.confirmSave().then(({ value: isConfirmed }) => {
        if (!isConfirmed) return;
        this.isSaving = true;
        const payload = {
          is_enabled: this.form.is_enabled,
          expiration_months: this.form.is_enabled
            ? Number(this.form.expiration_months)
            : null
        };
        savePointExpireConfig(this.companyId, payload)
          .then(() => {
            Swal.fire({
              icon: "success",
              text: "บันทึกการตั้งค่าแต้มหมดอายุเรียบร้อยแล้ว",
              confirmButtonText: "ตกลง"
            });
            this.$emit("saved");
            return this.loadConfig({ showSpinner: false });
          })
          .catch((error) => {
            Swal.fire({
              icon: "error",
              title: "บันทึกการตั้งค่าไม่สำเร็จ",
              text: `${error.message} การตั้งค่าเดิมยังคงมีผล`,
              confirmButtonText: "ตกลง"
            });
          })
          .finally(() => {
            this.isSaving = false;
          });
      });
    }
  }
};
</script>
